'use client'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import './customSelect.css'
import { Search } from 'react-feather'
import {
  CustomSelectProps,
  OptionType
} from '@/app/interfaces/formDataInterface'

type CustomSelectOption = string | OptionType

const CustomSelect: React.FC<CustomSelectProps> = ({
  id,
  placeholder,
  options,
  selected,
  onSelect,
  isSearchable = true,
  disabled = false,
  label,
  floating = false,
  className = '',
  showDividerBeforeOptionLink = false,
  styleOptionLink = false,
  icon
}) => {
  const [isDropdownActive, setDropdownActive] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>(
    'bottom'
  )
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const selectRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<(HTMLDivElement | null)[]>([])

  const handleSelect = useCallback(
    (value: string) => {
      if (disabled) return
      onSelect(value)
      setDropdownActive(false)
      setSearchTerm('')
      setHighlightedIndex(-1)
    },
    [disabled, onSelect]
  )

  // Helper function to get option value
  const getOptionValue = (option: CustomSelectOption): string => {
    return typeof option === 'string' ? option : option.value
  }

  // Helper function to get option label
  const getOptionLabel = (option: CustomSelectOption): string => {
    return typeof option === 'string' ? option : option.label
  }

  // Get the currently selected option label.
  const getSelectedLabel = (): string => {
    const selectedOption = options.find(
      (option) => getOptionValue(option) === selected
    )
    return selectedOption ? getOptionLabel(selectedOption) : placeholder
  }

  const filteredOptions = options.filter((option) =>
    getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
  )

  const toggleDropdown = () => {
    if (disabled) return
    setDropdownActive(!isDropdownActive)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setHighlightedIndex(0)
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(e.target as Node)) {
        setDropdownActive(false)
        setHighlightedIndex(-1)
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isDropdownActive) return

      if (e.key === 'Escape') {
        setDropdownActive(false)
        setHighlightedIndex(-1)
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        )
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        )
      } else if (e.key === 'Enter' && highlightedIndex >= 0) {
        e.preventDefault()
        handleSelect(getOptionValue(filteredOptions[highlightedIndex]))
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isDropdownActive, filteredOptions, highlightedIndex, handleSelect])

  useEffect(() => {
    if (selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect()
      const isSpaceBelow = window.innerHeight - rect.bottom > 200
      setDropdownPosition(isSpaceBelow ? 'bottom' : 'top')
    }
  }, [isDropdownActive])

  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      })
    }
  }, [highlightedIndex])

  return (
    <div
      className={`custom-select-container cursor-pointer ${disabled ? 'disabled' : ''} ${className}`}
    >
      {/* Label rendering */}
      {label && (
        <label
          className={`custom-select-label ${
            floating && (selected || isDropdownActive) ? 'float' : ''
          } ${!floating ? 'static' : ''}`}
          htmlFor={id}
        >
          {label}
        </label>
      )}

      {/* Select container */}
      <div
        className={`custom-select ${disabled ? 'disabled' : ''}`}
        id={id}
        onClick={toggleDropdown}
        ref={selectRef}
        tabIndex={0}
        style={{
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto'
        }}
      >
        {/* Selected value display */}
        <div
          className={`select-selected ${isDropdownActive ? 'select-arrow-active' : ''}`}
        >
          {/* Conditionally render placeholder (with icon) or the selected option label */}

          {selected == null ? (
            <div className="d-flex align-items-center gap-2">
              <span className="select-icon">{icon}</span>
              <span>{placeholder}</span>
            </div>
          ) : (
            <span>{getSelectedLabel()}</span>
          )}
        </div>

        {/* Dropdown options */}
        {isDropdownActive && (
          <div className={`select-items select-items-${dropdownPosition}`}>
            {/* Search input */}
            {isSearchable && (
              <div className="search-input-wrapper">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  className="search-input form-control"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Search..."
                  autoFocus
                />
              </div>
            )}

            {/* Options list */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const isOptionLink = index === filteredOptions.length - 1
                return (
                  <React.Fragment key={index}>
                    {/* Add divider before option link if enabled */}
                    {showDividerBeforeOptionLink && isOptionLink && <hr />}

                    <div
                      ref={(el) => (optionRefs.current[index] = el)}
                      className={`select-option ${index === highlightedIndex ? 'highlighted' : ''} ${styleOptionLink && isOptionLink ? 'text-primary option-link' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleSelect(getOptionValue(option))
                      }}
                    >
                      {getOptionLabel(option)}
                    </div>
                  </React.Fragment>
                )
              })
            ) : (
              <div className="no-options">No options available</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default CustomSelect
